import { ListHelpers } from '@helpers/list-numbering-helpers.js';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — JS module without type declarations
import { calculateResolvedParagraphProperties } from '../../extensions/paragraph/resolvedPropertiesCache.js';
import type { Editor } from '../../core/Editor.js';
import { getListOrdinalFromPath, getListRendering } from '@superdoc/common/list-rendering';
import type {
  BlockNodeAddress,
  ListItemAddress,
  ListItemInfo,
  ListKind,
  ListsListQuery,
  ListsListResult,
} from '@superdoc/document-api';
import { buildResolvedHandle, buildDiscoveryItem, buildDiscoveryResult } from '@superdoc/document-api';
import { DocumentApiAdapterError } from '../errors.js';
import { getRevision } from '../plan-engine/revision-tracker.js';
import { getBlockIndex } from './index-cache.js';
import { validatePaginationInput } from './adapter-utils.js';
import { computeSequenceIdMap } from './list-sequence-helpers.js';
import type { BlockCandidate, BlockIndex } from './node-address-resolver.js';
import { toFiniteNumber } from './value-utils.js';

export type ListItemProjection = {
  candidate: BlockCandidate;
  address: ListItemAddress;
  numId?: number;
  level?: number;
  marker?: string;
  path?: number[];
  ordinal?: number;
  kind?: ListKind;
  text?: string;
};

function getNumberingProperties(node: BlockCandidate['node']): { numId?: number; level?: number } {
  const attrs = (node.attrs ?? {}) as {
    paragraphProperties?: { numberingProperties?: { numId?: unknown; ilvl?: unknown } | null } | null;
    numberingProperties?: { numId?: unknown; ilvl?: unknown } | null;
  };

  const paragraphNumbering = attrs.paragraphProperties?.numberingProperties ?? undefined;
  const fallbackNumbering = attrs.numberingProperties ?? undefined;
  const numId = toFiniteNumber(paragraphNumbering?.numId ?? fallbackNumbering?.numId);
  const level = toFiniteNumber(paragraphNumbering?.ilvl ?? fallbackNumbering?.ilvl);
  return { numId, level };
}

function deriveListKindFromDefinitions(editor: Editor, numId?: number, level?: number): ListKind | undefined {
  if (numId == null || level == null || !editor.converter) return undefined;
  try {
    const details = ListHelpers.getListDefinitionDetails({ numId, level, listType: undefined, editor });
    const numberingType = typeof details?.listNumberingType === 'string' ? details.listNumberingType : undefined;
    if (numberingType === 'bullet') return 'bullet';
    if (typeof numberingType === 'string' && numberingType.length > 0) return 'ordered';
    return undefined;
  } catch {
    return undefined;
  }
}

function deriveListKind(
  editor: Editor,
  candidate: BlockCandidate,
  numId?: number,
  level?: number,
): ListKind | undefined {
  const listRendering = (candidate.node.attrs ?? {}) as {
    listRendering?: {
      numberingType?: unknown;
    } | null;
  };
  const numberingType = listRendering.listRendering?.numberingType;
  if (numberingType === 'bullet') return 'bullet';
  if (typeof numberingType === 'string' && numberingType.length > 0) return 'ordered';
  return deriveListKindFromDefinitions(editor, numId, level);
}

function getListText(candidate: BlockCandidate): string | undefined {
  const text = (candidate.node as { textContent?: unknown }).textContent;
  return typeof text === 'string' ? text : undefined;
}

export function projectListItemCandidate(editor: Editor, candidate: BlockCandidate): ListItemProjection {
  const attrs = (candidate.node.attrs ?? {}) as {
    listRendering?: unknown;
  };

  let { numId, level } = getNumberingProperties(candidate.node);
  if (numId == null) {
    // Style-linked numbering: the node renders a marker but carries no numPr
    // on its attrs — the effective numbering lives in the resolved property
    // chain (same resolver the numbering plugin uses). Without this fallback,
    // lists.attach/setType reject blocks that visibly ARE numbered clauses.
    try {
      const resolved = calculateResolvedParagraphProperties(
        editor,
        candidate.node,
        editor.state.doc.resolve(candidate.pos),
      ) as { numberingProperties?: { numId?: unknown; ilvl?: unknown } | null } | null;
      const effective = resolved?.numberingProperties;
      if (effective) {
        numId = toFiniteNumber(effective.numId);
        level = toFiniteNumber(effective.ilvl) ?? level ?? 0;
      }
    } catch {
      // best-effort: fall through with attrs-only numbering
    }
  }
  const listRendering = getListRendering(attrs.listRendering);
  const path = listRendering?.path;
  const ordinal = getListOrdinalFromPath(path);
  const marker = listRendering?.markerText;

  return {
    candidate,
    address: {
      kind: 'block',
      nodeType: 'listItem',
      nodeId: candidate.nodeId,
    },
    numId,
    level,
    kind: deriveListKind(editor, candidate, numId, level),
    marker,
    path,
    ordinal,
    text: getListText(candidate),
  };
}

export function listItemProjectionToInfo(projection: ListItemProjection, listId: string): ListItemInfo {
  return {
    address: projection.address,
    listId,
    marker: projection.marker,
    ordinal: projection.ordinal,
    path: projection.path,
    level: projection.level,
    kind: projection.kind,
    text: projection.text,
  };
}

function matchesListQuery(projection: ListItemProjection, query?: ListsListQuery): boolean {
  if (!query) return true;
  if (query.kind && projection.kind !== query.kind) return false;
  if (query.level != null && projection.level !== query.level) return false;
  if (query.ordinal != null && projection.ordinal !== query.ordinal) return false;
  return true;
}

export function resolveBlockScopeRange(
  index: BlockIndex,
  within?: BlockNodeAddress,
): { start: number; end: number } | undefined {
  if (!within) return undefined;

  const matches = index.candidates.filter(
    (candidate) => candidate.nodeType === within.nodeType && candidate.nodeId === within.nodeId,
  );
  if (matches.length === 0) {
    throw new DocumentApiAdapterError('TARGET_NOT_FOUND', 'List scope block was not found.', {
      within,
    });
  }
  if (matches.length > 1) {
    throw new DocumentApiAdapterError('INVALID_TARGET', 'List scope block id is ambiguous.', {
      within,
      count: matches.length,
    });
  }

  return {
    start: matches[0]!.pos,
    end: matches[0]!.end,
  };
}

function isWithinScope(candidate: BlockCandidate, scope: { start: number; end: number } | undefined): boolean {
  if (!scope) return true;
  return candidate.pos >= scope.start && candidate.end <= scope.end;
}

function listItemCandidatesInScope(
  index: BlockIndex,
  scope: { start: number; end: number } | undefined,
): BlockCandidate[] {
  return index.candidates.filter((candidate) => candidate.nodeType === 'listItem' && isWithinScope(candidate, scope));
}

export function buildListItemIndex(editor: Editor): { index: BlockIndex; items: ListItemProjection[] } {
  const index = getBlockIndex(editor);
  const items = index.candidates
    .filter((candidate) => candidate.nodeType === 'listItem')
    .map((candidate) => projectListItemCandidate(editor, candidate));
  return { index, items };
}

export function listListItems(editor: Editor, query?: ListsListQuery): ListsListResult {
  if (query?.within && query.within.kind !== 'block') {
    throw new DocumentApiAdapterError('INVALID_TARGET', 'lists.list only supports block within scopes.', {
      within: query.within,
    });
  }

  validatePaginationInput(query?.offset, query?.limit);

  const index = getBlockIndex(editor);
  const scope = resolveBlockScopeRange(index, query?.within as BlockNodeAddress | undefined);
  const allCandidates = index.candidates.filter((candidate) => candidate.nodeType === 'listItem');
  const allProjections = allCandidates.map((candidate) => projectListItemCandidate(editor, candidate));
  const projections = allProjections.filter((projection) => isWithinScope(projection.candidate, scope));
  const safeOffset = query?.offset ?? 0;
  const safeLimit = query?.limit ?? Number.POSITIVE_INFINITY;
  const pageEnd = safeOffset + safeLimit;
  const evaluatedRevision = getRevision(editor);

  // Compute sequence IDs from document-wide projections so list identity is
  // stable across scoped queries.
  const sequenceIds = computeSequenceIdMap(allProjections);

  let total = 0;
  const items: ListsListResult['items'] = [];

  for (const projection of projections) {
    if (!matchesListQuery(projection, query)) continue;

    const currentIndex = total;
    total += 1;
    if (currentIndex < safeOffset || currentIndex >= pageEnd) continue;

    const listId = sequenceIds.get(projection.address.nodeId) ?? '';
    const info = listItemProjectionToInfo(projection, listId);
    const handle = buildResolvedHandle(info.address.nodeId, 'stable', 'list');
    const { address, marker, ordinal, path, level, kind, text } = info;
    items.push(
      buildDiscoveryItem(info.address.nodeId, handle, { address, listId, marker, ordinal, path, level, kind, text }),
    );
  }

  return buildDiscoveryResult({
    evaluatedRevision,
    total,
    items,
    page: { limit: query?.limit ?? total, offset: safeOffset, returned: items.length },
  });
}

function hasNumberingMetadata(candidate: BlockCandidate): boolean {
  const { numId } = getNumberingProperties(candidate.node);
  if (numId != null) return true;
  const attrs = (candidate.node.attrs ?? {}) as { listRendering?: unknown };
  return getListRendering(attrs.listRendering) != null;
}

export function resolveListItem(editor: Editor, address: ListItemAddress): ListItemProjection {
  const index = getBlockIndex(editor);
  let matches = index.candidates.filter(
    (candidate) => candidate.nodeType === 'listItem' && candidate.nodeId === address.nodeId,
  );

  if (matches.length === 0) {
    // Numbered paragraphs/headings ARE list participants in Word terms (the
    // editor's own list toolbar treats them as such): legal clause numbering
    // ("2.3.") lives on heading-styled paragraphs that block classification
    // maps to 'heading', making them unreachable for lists.attach/setType by
    // nodeId. Fall back to any block carrying numbering metadata.
    matches = index.candidates.filter(
      (candidate) => candidate.nodeId === address.nodeId && hasNumberingMetadata(candidate),
    );
  }

  if (matches.length === 0) {
    throw new DocumentApiAdapterError('TARGET_NOT_FOUND', 'List item target was not found.', {
      target: address,
    });
  }

  if (matches.length > 1) {
    throw new DocumentApiAdapterError('INVALID_TARGET', 'List item target id is ambiguous.', {
      target: address,
      count: matches.length,
    });
  }

  return projectListItemCandidate(editor, matches[0]!);
}
